import { useEffect } from "react";

interface HreflangAlt {
  hreflang: string;
  path: string; // path starting with "/"
}

interface SeoProps {
  title: string;
  description: string;
  canonicalPath?: string; // path starting with "/"
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
  hreflangs?: HreflangAlt[];
}

const SITE = "https://decorcanvas.design";
const DEFAULT_OG = "https://storage.googleapis.com/gpt-engineer-file-uploads/WbgKg71MxpOWvvwsQwakrAzwtfv1/social-images/social-1768120091354-download (17).png";

const upsertMeta = (selector: string, attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * SEO helper: updates <title>, meta description, canonical, OG/Twitter tags,
 * and optional JSON-LD for the current route. Pure presentation/SEO — no
 * impact on app logic.
 */
export const Seo = ({ title, description, canonicalPath, ogImage, jsonLd, noindex, hreflangs }: SeoProps) => {
  useEffect(() => {
    document.title = title;

    const canonical = canonicalPath ? `${SITE}${canonicalPath}` : `${SITE}${window.location.pathname}`;
    const image = ogImage || DEFAULT_OG;

    // Auto-noindex on non-production hosts (lovable.app preview/published, localhost)
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const isProdHost = host === "decorcanvas.design" || host === "www.decorcanvas.design";
    const shouldNoindex = noindex || !isProdHost;

    upsertMeta('meta[name="description"]', "name", "description", description);
    upsertMeta('meta[name="robots"]', "name", "robots", shouldNoindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");
    upsertLink("canonical", canonical);

    // hreflang alternates — replace any previously injected ones
    document.head.querySelectorAll('link[rel="alternate"][data-seo="hreflang"]').forEach((n) => n.remove());
    if (hreflangs && hreflangs.length) {
      hreflangs.forEach(({ hreflang, path }) => {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", hreflang);
        link.setAttribute("href", `${SITE}${path}`);
        link.setAttribute("data-seo", "hreflang");
        document.head.appendChild(link);
      });
    }

    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);
    upsertMeta('meta[property="og:image"]', "property", "og:image", image);

    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", image);

    // JSON-LD: replace any previously-injected route-level schema
    const PREV_ID = "route-jsonld";
    document.head.querySelectorAll(`script[data-seo="${PREV_ID}"]`).forEach((n) => n.remove());
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((block) => {
        const tag = document.createElement("script");
        tag.type = "application/ld+json";
        tag.dataset.seo = PREV_ID;
        tag.text = JSON.stringify(block);
        document.head.appendChild(tag);
      });
    }
  }, [title, description, canonicalPath, ogImage, jsonLd, noindex, hreflangs]);

  return null;
};

export default Seo;
